import * as THREE from 'three';
import type { BattleRules, BattleState } from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { TAIL_SEA_Y } from '../omens/tail.ts';
import type { OmenCtx } from '../omens/common.ts';
import { WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';

/**
 * The lash (El Cuerno 2, docs/SAGAS.md): the titan swings its tail with the clan's camp on it. Before each swing the
 * ground creaks, dust lifts and the view leans the way it will go; then everything loose slides: dust streaks along
 * the ground, the camera tilted, the posts' rings bright and a rope drawn taut to each hero holding on. The clan's
 * barrels tip over and roll, knocking down what they meet; what goes over the rim (a barrel, a nightmare) drops to the
 * sea far below with a splash. The last swing is the big one: the view pulls out over the whole tail for a moment.
 * (omens/tail.ts builds the camp and the posts themselves.)
 *
 * Draw calls: one instanced barrel, one instanced rope, the posts' rings (one mesh), one glow layer. No lights. The
 * camera's lean and pull are applied over the table camera each frame (`camera`), never stored in it.
 */

const K = WORLD_PER_HEX / 1000;
const RING = new THREE.Color('#e4ecf4');
const DUST = '#8a8478';
const FOAM = '#eef4f8';
const BARREL_R = 0.2;
const BARREL_H = 0.5;
const MAX_ROPES = 8;
type Lash = NonNullable<BattleRules['lash']>;

interface Roll {
  px: number;
  py: number;
  cx: number;
  cy: number;
  /** Tipped over (it lies from its first roll on), its spin and the way it last rolled (world, radians). */
  down: boolean;
  spin: number;
  yaw: number;
}

interface Drop {
  p: THREE.Vector3;
  out: THREE.Vector3;
  at: number;
  yaw: number;
}

/** The clan's barrel: staves, two iron hoops, the clan's red band; built standing, its middle at the origin. */
const barrelGeo = (): THREE.BufferGeometry => {
  const kit = new Kit();
  kit.add(new THREE.CylinderGeometry(BARREL_R * 0.86, BARREL_R * 0.86, BARREL_H, 10), '#6e5240', new THREE.Matrix4());
  kit.add(new THREE.CylinderGeometry(BARREL_R, BARREL_R, BARREL_H * 0.55, 10), '#7a5c46', new THREE.Matrix4());
  for (const y of [-0.17, 0.17]) kit.add(new THREE.CylinderGeometry(BARREL_R * 1.02, BARREL_R * 1.02, 0.035, 10), '#3e4246', place(0, y, 0));
  kit.add(new THREE.CylinderGeometry(BARREL_R * 1.015, BARREL_R * 1.015, 0.05, 10), '#a43a2c', place(0, 0.02, 0));
  for (const y of [-0.25, 0.25]) kit.add(new THREE.CylinderGeometry(BARREL_R * 0.8, BARREL_R * 0.8, 0.01, 10), '#5a4232', place(0, y, 0));
  const mesh = kit.build(new THREE.MeshBasicMaterial(), 'barrel') as THREE.Mesh;
  (mesh.material as THREE.Material).dispose();
  return mesh.geometry;
};

const reduced = (): boolean => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export class LashLayer {
  readonly group = new THREE.Group();
  private barrels: THREE.InstancedMesh;
  private ropes: THREE.InstancedMesh;
  private mat: THREE.MeshStandardMaterial;
  private ropeMat: THREE.MeshStandardMaterial;
  private rings: THREE.Mesh;
  private ringMat: THREE.MeshBasicMaterial;
  private glow: GlowLayer;
  private rolls = new Map<number, Roll>();
  private drops: Drop[] = [];
  private splashes: { p: THREE.Vector3; at: number; big: boolean }[] = [];
  private posts: THREE.Vector3[];
  /** Heroes holding on through this slide, by entity id: the post each holds. */
  private held = new Map<number, THREE.Vector3>();
  private sliding = false;
  private now = 0;
  /** The swing being felt: its way on the ground (world), when the warning and the slide began, whether it is the big one. */
  private dir = new THREE.Vector3(1, 0, 0);
  private warnAt = -100;
  private slideAt = -100;
  private bigAt = -100;
  private calm = reduced();
  private m = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private q2 = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s3 = new THREE.Vector3();
  private e = new THREE.Euler();
  private fog: { near: number; far: number } | null = null;

  constructor(
    private ctx: OmenCtx,
    private r: Lash,
  ) {
    this.group.name = 'lash';
    this.mat = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.85, flatShading: true });
    this.barrels = new THREE.InstancedMesh(barrelGeo(), this.mat, Math.max(1, r.barrels.length));
    this.barrels.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.barrels.frustumCulled = false;
    this.barrels.count = 0;
    this.group.add(this.barrels);
    this.ropeMat = new THREE.MeshStandardMaterial({ color: '#c8bca0', roughness: 1 });
    this.ropes = new THREE.InstancedMesh(new THREE.CylinderGeometry(0.025, 0.025, 1, 5).translate(0, 0.5, 0), this.ropeMat, MAX_ROPES);
    this.ropes.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.ropes.frustumCulled = false;
    this.ropes.count = 0;
    this.group.add(this.ropes);
    // The posts' reach on the ground: a pale ring round each, brighter while the tail is about to swing or swinging.
    this.posts = r.posts.map((p) => this.ctx.at(p.x, p.y, 0));
    const R = r.postR * K;
    const geos = this.posts.map((p) => new THREE.RingGeometry(R * 0.9, R, 40).rotateX(-Math.PI / 2).translate(p.x, ctx.ground(p.x, p.z) + 0.06, p.z));
    const merged = new THREE.BufferGeometry();
    if (geos.length) {
      const pos: number[] = [];
      for (const g of geos) {
        const ng = g.index ? g.toNonIndexed() : g;
        const a = ng.getAttribute('position');
        for (let i = 0; i < a.count; i++) pos.push(a.getX(i), a.getY(i), a.getZ(i));
        g.dispose();
        if (ng !== g) ng.dispose();
      }
      merged.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    }
    this.ringMat = new THREE.MeshBasicMaterial({ color: RING, transparent: true, opacity: 0.3, depthWrite: false });
    this.rings = new THREE.Mesh(merged, this.ringMat);
    this.rings.renderOrder = 6;
    this.rings.frustumCulled = false;
    this.group.add(this.rings);
    this.glow = new GlowLayer(ctx.low ? 16 : 32);
    this.group.add(this.glow.mesh);
  }

  private worldDir(a: number): THREE.Vector3 {
    const t = (a / 1024) * Math.PI * 2;
    // Sim (x, y) → world (x, z).
    const o = this.ctx.at(0, 0, 0);
    const p = this.ctx.at(Math.cos(t) * 1000, Math.sin(t) * 1000, 0);
    return this.dir.set(p.x - o.x, 0, p.z - o.z).normalize();
  }

  /** The lash's moments: the warning, the slide (or the big one), something over the rim, a barrel knocking one down. */
  event(kind: 'warn' | 'slide' | 'big' | 'fall' | 'barrel' | 'crush', x: number, y: number, a?: number): void {
    const low = this.ctx.low;
    if (kind === 'warn') {
      if (a !== undefined) this.worldDir(a);
      this.warnAt = this.now;
      this.ctx.shake(0.12);
      // Dust lifting off the ground round the camp, and off each post's foot.
      for (const p of this.posts) this.ctx.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 2 : 4, color: DUST, speed: 0.01, up: 0.015, size: 0.3, life: 30, smoke: true, spread: 0.5 });
      const c = this.ctx.at(0, 0, 0.1);
      this.ctx.vfx.burst(c, { n: low ? 4 : 10, color: DUST, speed: 0.03, up: 0.01, size: 0.4, life: 36, smoke: true, spread: 3 });
    } else if (kind === 'slide' || kind === 'big') {
      if (a !== undefined) this.worldDir(a);
      this.slideAt = this.now;
      if (kind === 'big') this.bigAt = this.now;
      this.ctx.shake(kind === 'big' ? 0.45 : 0.22);
    } else if (kind === 'fall') {
      // Over the rim: it drops to the sea, and the splash comes up a moment later far below.
      const p = this.ctx.at(x, y, 0);
      const out = this.v.set(p.x, 0, p.z).normalize();
      const sea = new THREE.Vector3(p.x + out.x * 1.6, TAIL_SEA_Y + 0.05, p.z + out.z * 1.6);
      this.splashes.push({ p: sea, at: this.now + 0.85, big: false });
      this.ctx.vfx.burst(p.clone().setY(p.y + 0.1), { n: low ? 3 : 6, color: '#6a6e72', speed: 0.03, up: 0.03, size: 0.12, life: 20, gravity: 0.01, spread: 0.3 });
    } else if (kind === 'crush') {
      const p = this.ctx.at(x, y, 0.3);
      this.ctx.vfx.burst(p, { n: low ? 4 : 8, color: '#8a6e58', speed: 0.05, up: 0.05, size: 0.09, life: 16, gravity: 0.008, spread: 0.3 });
      this.ctx.vfx.burst(p, { n: low ? 2 : 4, color: DUST, speed: 0.02, up: 0.02, size: 0.35, life: 24, smoke: true, spread: 0.3 });
    }
  }

  /** Every fresh sim tick: the barrels where they were and are (and the ones gone over), who holds on, dust while it slides. */
  tick(s: BattleState): void {
    const st = s.lash;
    const seen = new Set<number>();
    for (const b of st?.barrels ?? []) {
      seen.add(b.id);
      const t = this.rolls.get(b.id);
      if (!t) {
        this.rolls.set(b.id, { px: b.x, py: b.y, cx: b.x, cy: b.y, down: false, spin: (b.id * 1.7) % 6.28, yaw: 0 });
        continue;
      }
      const dx = b.x - t.cx;
      const dy = b.y - t.cy;
      t.px = t.cx;
      t.py = t.cy;
      t.cx = b.x;
      t.cy = b.y;
      if (dx || dy) {
        t.down = true;
        const p0 = this.ctx.at(t.px, t.py, 0);
        const p1 = this.ctx.at(b.x, b.y, 0);
        t.yaw = Math.atan2(p1.x - p0.x, p1.z - p0.z);
        if (b.roll > 0 && (s.tick + b.id) % 2 === 0) this.ctx.vfx.burst(p1.clone().setY(p1.y + 0.05), { n: this.ctx.low ? 1 : 2, color: DUST, speed: 0.01, up: 0.01, size: 0.25, life: 22, smoke: true, spread: 0.2 });
      }
    }
    // Gone over the rim: it drops from where it was.
    for (const [id, t] of [...this.rolls]) {
      if (seen.has(id)) continue;
      const p = this.ctx.at(t.cx, t.cy, 0);
      this.drops.push({ p: p.setY(p.y + BARREL_R), out: new THREE.Vector3(p.x, 0, p.z).normalize(), at: this.now, yaw: t.yaw });
      this.rolls.delete(id);
    }
    const sliding = (st?.slide ?? 0) > 0;
    if (sliding && !this.sliding) {
      // Who holds on: a standing hero inside a post's reach as it goes.
      this.held.clear();
      const R = this.r.postR;
      for (const h of s.heroes) {
        if (h.downTicks > 0) continue;
        const i = this.r.posts.findIndex((p) => Math.hypot(p.x - h.x, p.y - h.y) <= R);
        if (i >= 0) this.held.set(h.id, this.posts[i] as THREE.Vector3);
      }
    }
    if (!sliding && this.sliding) this.held.clear();
    this.sliding = sliding;
    // Dust streaking along the ground behind whatever slides.
    if (sliding && st && s.tick % 2 === 0) {
      const n = this.ctx.low ? 6 : 14;
      let k = 0;
      for (const e of s.enemies) {
        if (k >= n) break;
        if (e.hp <= 0) continue;
        this.ctx.vfx.burst(this.ctx.at(e.x, e.y, 0.08), { n: 1, color: DUST, speed: 0.006, up: 0.006, size: 0.32, life: 20, smoke: true, spread: 0.25 });
        k++;
      }
      for (const h of s.heroes) if ((h.shove ?? 0) > 0) this.ctx.vfx.burst(this.ctx.at(h.x, h.y, 0.08), { n: 2, color: DUST, speed: 0.008, up: 0.008, size: 0.3, life: 20, smoke: true, spread: 0.25 });
    }
  }

  /** Every redraw: the barrels (standing, rolling, dropping), the ropes to the heroes holding on, the posts' glow. */
  draw(alpha: number, contact: (x: number, y: number, z: number, size: number) => void): void {
    let n = 0;
    for (const t of this.rolls.values()) {
      const x = t.px + (t.cx - t.px) * alpha;
      const y = t.py + (t.cy - t.py) * alpha;
      const g = this.ctx.at(x, y, 0);
      if (t.down) {
        // Lying across its way, turning as it goes.
        const moved = Math.hypot(t.cx - t.px, t.cy - t.py) * K;
        t.spin += (moved / BARREL_R) * 0.08;
        this.q.setFromAxisAngle(this.v.set(0, 1, 0), t.yaw);
        this.q2.setFromAxisAngle(this.v.set(0, 0, 1), Math.PI / 2);
        this.q.multiply(this.q2);
        this.q2.setFromAxisAngle(this.v.set(0, 1, 0), t.spin);
        this.q.multiply(this.q2);
        this.m.compose(this.s3.set(g.x, g.y + BARREL_R, g.z), this.q, this.v.set(1, 1, 1));
      } else {
        this.q.setFromAxisAngle(this.v.set(0, 1, 0), t.spin);
        this.m.compose(this.s3.set(g.x, g.y + BARREL_H / 2, g.z), this.q, this.v.set(1, 1, 1));
      }
      this.barrels.setMatrixAt(n++, this.m);
      contact(g.x, g.y, g.z, 0.4);
    }
    // Dropping off the rim: on out a little, then down to the sea, tumbling.
    this.drops = this.drops.filter((d) => {
      const t = this.now - d.at;
      if (t > 1.0 || n >= this.barrels.instanceMatrix.count) return false;
      const y = d.p.y - 5.2 * t * t;
      if (y < TAIL_SEA_Y) {
        this.splashes.push({ p: new THREE.Vector3(d.p.x + d.out.x * (0.9 + t * 1.4), TAIL_SEA_Y + 0.05, d.p.z + d.out.z * (0.9 + t * 1.4)), at: this.now, big: false });
        return false;
      }
      this.e.set(t * 4, d.yaw, Math.PI / 2 + t * 3);
      this.q.setFromEuler(this.e);
      this.m.compose(this.s3.set(d.p.x + d.out.x * (0.9 + t * 1.4), y, d.p.z + d.out.z * (0.9 + t * 1.4)), this.q, this.v.set(1, 1, 1));
      this.barrels.setMatrixAt(n++, this.m);
      return true;
    });
    this.barrels.count = n;
    this.barrels.instanceMatrix.needsUpdate = true;
    // A rope drawn taut from each post to the hero holding on.
    let r = 0;
    if (this.sliding) {
      for (const [id, post] of this.held) {
        if (r >= MAX_ROPES) break;
        const h = this.ctx.pos(id);
        if (!h) continue;
        const from = this.s3.set(post.x, post.y + 1.0, post.z);
        const to = this.v.set(h.x, h.y + 0.55, h.z);
        const d = to.clone().sub(from);
        const len = d.length();
        if (len < 0.05) continue;
        this.q.setFromUnitVectors(new THREE.Vector3(0, 1, 0), d.normalize());
        this.m.compose(from, this.q, new THREE.Vector3(1, len, 1));
        this.ropes.setMatrixAt(r++, this.m);
      }
    }
    this.ropes.count = r;
    this.ropes.instanceMatrix.needsUpdate = true;
  }

  /** Every frame: the rings, the splashes due, and the view's lean and pull over the table camera. */
  frame(now: number, camera?: THREE.Camera): void {
    this.now = now;
    const warn = this.warnAt > this.slideAt && now - this.warnAt < 3.2;
    const slide = now - this.slideAt < 1.6;
    this.ringMat.opacity = warn ? 0.55 + 0.35 * Math.abs(Math.sin(now * 7)) : slide ? 0.8 : 0.28 + 0.08 * Math.sin(now * 1.6);
    this.glow.begin();
    for (const p of this.posts) {
      const k = warn ? 0.35 + 0.25 * Math.abs(Math.sin(now * 7)) : slide ? 0.5 : 0.08;
      this.glow.add(p.x, p.y + 0.3, p.z, RING, 2.6, k * 0.4);
    }
    // A glint at each post where a hero holds on.
    if (this.sliding) for (const post of this.held.values()) this.glow.add(post.x, post.y + 1.05, post.z, RING, 0.9, 0.6);
    this.glow.end();
    const low = this.ctx.low;
    this.splashes = this.splashes.filter((sp) => {
      if (sp.at > now) return true;
      this.ctx.vfx.burst(sp.p, { n: low ? 8 : 18, color: FOAM, speed: 0.04, up: 0.12, size: 0.14, life: 26, gravity: 0.01, spread: 0.3 });
      this.ctx.vfx.ring(sp.p, 0.2, 1.6, FOAM, 22, 0.7);
      return false;
    });
    if (camera) this.lean(now, camera);
  }

  /**
   * The view leans the way the tail is about to go, more as it goes, and eases back; the big one pulls it out over the
   * whole tail for a moment. Applied over the table camera's own placement this frame (it redoes it every frame).
   */
  private lean(now: number, camera: THREE.Camera): void {
    const tw = now - this.warnAt;
    const ts = now - this.slideAt;
    const tb = now - this.bigAt;
    const ease = (x: number): number => (x <= 0 ? 0 : x >= 1 ? 1 : x * x * (3 - 2 * x));
    // Leaning: in over the warning, deeper through the slide, back out after it.
    let roll = 0;
    if (tw >= 0 && tw < 3.2 && this.warnAt > this.slideAt) roll = 0.035 * ease(tw / 2.5);
    if (ts >= 0 && ts < 2.4) roll = (this.bigAt === this.slideAt ? 0.085 : 0.06) * (ts < 0.9 ? Math.max(ease(ts / 0.3), 0.6) : 1 - ease((ts - 0.9) / 1.5));
    // Pulled out: two seconds out over the tail, a breath, back in.
    let pull = 0;
    if (tb >= 0 && tb < 4.2) pull = tb < 1.6 ? ease(tb / 1.6) : tb < 2.6 ? 1 : 1 - ease((tb - 2.6) / 1.6);
    if (this.calm) {
      roll = 0;
      pull *= 0.5;
    }
    const fog = this.ctx.scene.fog instanceof THREE.Fog ? this.ctx.scene.fog : null;
    if (pull > 0 && fog && !this.fog) this.fog = { near: fog.near, far: fog.far };
    if (fog && this.fog) {
      fog.near = this.fog.near * (1 + pull * 1.5);
      fog.far = this.fog.far * (1 + pull * 1.8);
      if (pull === 0) this.fog = null;
    }
    if (roll === 0 && pull === 0) return;
    const fwd = camera.getWorldDirection(this.v);
    if (pull > 0) {
      // Out from where it looks on the ground, and up a little.
      const t = fwd.y < -0.01 ? -camera.position.y / fwd.y : 20;
      const look = camera.position.clone().addScaledVector(fwd, t);
      camera.position.sub(look).multiplyScalar(1 + pull * 1.3).add(look);
      camera.position.y += pull * 4;
      camera.lookAt(look);
    }
    if (roll !== 0) {
      // Which way the ground falls on screen: the swing's way along the camera's right.
      const right = this.s3.set(1, 0, 0).applyQuaternion(camera.quaternion);
      const side = Math.sign(right.dot(this.dir)) || 1;
      this.q.setFromAxisAngle(camera.getWorldDirection(this.v), side * roll);
      camera.quaternion.premultiply(this.q);
    }
    camera.updateMatrixWorld();
  }

  dispose(): void {
    const fog = this.ctx.scene.fog instanceof THREE.Fog ? this.ctx.scene.fog : null;
    if (fog && this.fog) {
      fog.near = this.fog.near;
      fog.far = this.fog.far;
    }
    this.barrels.geometry.dispose();
    this.barrels.dispose();
    this.mat.dispose();
    this.ropes.geometry.dispose();
    this.ropes.dispose();
    this.ropeMat.dispose();
    this.rings.geometry.dispose();
    this.ringMat.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
  }
}
